<template>
  <div class="top-header" role="heading">
    <h1>GS Purchase Invoice Approval</h1>
  </div>
  <div class="jwt-generator">
    <h2>JWT Token Generator</h2><!-- Claims Input -->
    <div class="demo-secret-input">
      <label for="demoSecret">JWT Secret(for testing!):</label>
      <input v-model="demoSecret" id="demoSecret" placeholder="Add JWT Secret for testing">
    </div>
    <div class="claims-input"><label for="claimName">Claim Name:</label><input v-model="claimName" id="claimName"
        placeholder="Enter claim name (e.g., 'name')" /><label for="claimValue">Claim Value:</label><input
        v-model="claimValue" id="claimValue" placeholder="Enter a claim value" />
      <button @click="addClaim">Add Claim</button>
    </div><!-- Claims List -->
    <div v-if="claims.length" class="claims-list">
      <h3>Claims:</h3>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Value</th>
            <th>Description</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(claim, index) in claims" :key="index">
            <td>{{ claim.name }}</td>
            <td>{{ claim.value }}</td>
            <td class="description">{{ claim.description }}</td>
            <td><button @click="removeClaim(index)" class="remove-btn">Remove</button></td>
          </tr>
        </tbody>
      </table>
    </div><!-- Generate Token -->
    <div class="generate-section">
      <button @click="generateToken">Generate JWT Token</button>
    </div><!-- Display Token -->
    <div v-if="token" class="token-display">
      <h3>Generated JWT Token:</h3>
      <textarea rows="4" cols="80" v-model="token" readonly></textarea>
      <a :href="`/invoice?token=${token}`" target="_blank">Open invoice</a>
    </div>
  </div>
</template>
<script>
export default {
  name: 'JwtGenerator',
  data() {
    return {
      demoSecret: '',
      claimName: '',
      claimValue: '',
      claims: [
        { name: 'company_id', value: '1234', description: 'Company identifier in Netvisor.' },
        { name: 'user_id', value: '245', description: 'User identifier in Netvisor.' },
        { name: 'aud', value: 'our-microservice-application-user-id', description: 'Audience – static application user id for testing.' },
        { name: 'iss', value: 'localhost', description: 'Issuer – base URL/domain of token origin.' },
        { name: 'action', value: 'approval', description: 'Workflow action; use approval to load invoice approval view.' },
        { name: 'invoiceId', value: '3517', description: 'Netvisor Invoice id to open. The backend does not use this. It has hardcoded value for testing.' },
        { name: 'sub', value: '31057', description: 'Subject (primary user identifier); maps to user record id.' },
      ],
      token: '',
    };
  },
  methods: {
    addClaim() {
      if (this.claimName && this.claimValue) {
        this.claims.push({ name: this.claimName, value: this.claimValue, description: 'Custom claim – add context as needed.' });
        this.claimName = '';
        this.claimValue = '';
      } else {
        alert('Please enter both claim name and value.');
      }
    },
    removeClaim(index) {
      this.claims.splice(index, 1);
    },
    generateToken() {
      // Prepare header
      const header = {
        alg: 'HS256',
        typ: 'JWT',
      };

      // Prepare payload with claims
      const payload = {};

      // Add user-defined claims
      this.claims.forEach((claim) => {
        payload[claim.name] = claim.value;
      });

      // Set issued at (iat) and expiration (exp)
      const issuedAt = Math.floor(Date.now() / 1000);
      const expiration = issuedAt + 30 * 60; // 30 minutes from issued time
      payload.iat = issuedAt;
      payload.exp = expiration;

      // Encode to base64Url
      const encodeBase64Url = (obj) =>
        btoa(JSON.stringify(obj))
          .replace(/=/g, '')
          .replace(/\+/g, '-')
          .replace(/\//g, '_');

      const encodedHeader = encodeBase64Url(header);
      const encodedPayload = encodeBase64Url(payload);

      // Secret key (for demo, using a static secret)
      //const secret = 'JWTSECRET23456645745';
      const secret = this.demoSecret;
      // Generate signature
      const signature = this.sign(encodedHeader, encodedPayload, secret);

      // Final token
      this.token = `${encodedHeader}.${encodedPayload}.${signature}`;
    },
    // HMAC SHA256 signature
    sign(encodedHeader, encodedPayload, secret) {
      const data = `${encodedHeader}.${encodedPayload}`;
      const hash = CryptoJS.HmacSHA256(data, secret);
      return CryptoJS.enc.Base64.stringify(hash)
        .replace(/=/g, '')
        .replace(/\+/g, '-')
        .replace(/\//g, '_');
    },
  },
  mounted() {
    // Load CryptoJS library dynamically
    if (typeof CryptoJS === 'undefined') {
      const script = document.createElement('script');
      script.src =
        'https://cdnjs.cloudflare.com/ajax/libs/crypto-js/4.1.1/crypto-js.min.js';
      document.head.appendChild(script);
    }
  },
};
</script>
<style scoped>
.top-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: linear-gradient(#007553, #284440);
  color: white;
  padding: 8px;
  height: 40px;
}

.top-header h1 {
  font-size: 1rem;
}

.jwt-generator {
  max-width: 600px;
  margin: 80px auto auto;
  font-family: Arial, sans-serif;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
}

.claims-input {
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
}

.claims-input label {
  margin-top: 10px;
}

.claims-input input {
  padding: 8px;
  margin-top: 4px;
}

h2,
h3 {
  font-size: 0.9rem;
}

button {
  margin-top: 10px;
  padding: 8px 12px;
  cursor: pointer;
}

.remove-btn {
  margin: 3px;
  padding: 3px;
}

.token-display textarea {
  width: 100%;
  height: 100px;
  margin-top: 10px;
}

/* Description column styling */
.claims-list table {
  width: 100%;
  border-collapse: collapse;
}

.claims-list th,
.claims-list td {
  padding: 4px 6px;
  vertical-align: top;
}

.claims-list .description {
  font-size: 0.7rem;
  color: #555;
  max-width: 220px;
}
</style>